/**
 * CORS proxy utility for handling cross-origin requests when running on external domains
 * This is a copy of the shared utility that couldn't be committed to the submodule
 */

/**
 * Check if a URL is cross-origin and would cause CORS issues
 */
function isCrossOrigin(url: string): boolean {
	try {
		const targetUrl = new URL(url);
		const currentUrl = new URL(window.location.href);
		return targetUrl.origin !== currentUrl.origin;
	} catch {
		return false; // If URL parsing fails, assume it's not cross-origin
	}
}

/**
 * Check if we're running on a ShapeDiver domain that should have CORS access
 */
function isOnShapeDiverDomain(): boolean {
	const hostname = window.location.hostname;
	return (
		hostname === "appbuilder.shapediver.com" ||
		hostname === "www.shapediver.com" ||
		hostname === "shapediver.com" ||
		hostname.endsWith(".shapediver.com") ||
		hostname === "localhost" ||
		hostname === "127.0.0.1"
	);
}

/**
 * Fetch with CORS fallback strategies
 */
export async function fetchWithCorsHandling(
	url: string,
	options: RequestInit = {},
	fetchFn: typeof fetch = fetch,
): Promise<Response> {
	// If not cross-origin or on ShapeDiver domain, use normal fetch
	if (!isCrossOrigin(url) || isOnShapeDiverDomain()) {
		return fetchFn(url, {...options, mode: "cors"});
	}

	// First attempt: try direct CORS request
	try {
		const response = await fetchFn(url, {...options, mode: "cors"});
		if (response.ok) {
			return response;
		}
	} catch (error) {
		console.warn(
			"Direct CORS request failed, trying fallback strategies:",
			error,
		);
	}

	// Fallback 1: Try no-cors mode (limited functionality)
	try {
		console.log("Trying no-cors mode for:", url);
		const response = await fetchFn(url, {...options, mode: "no-cors"});
		// Note: no-cors responses are opaque, but we can try to use them
		return response;
	} catch (error) {
		console.warn("No-cors request failed:", error);
	}

	// Fallback 2: Use a public CORS proxy for theme files
	if (url.includes("theme") && url.includes(".json")) {
		try {
			const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`;
			console.log("Trying CORS proxy for theme file:", proxyUrl);

			const response = await fetchFn(proxyUrl);
			if (response.ok) {
				const data = await response.json();
				// Create a fake Response object with the proxied data
				return new Response(data.contents, {
					status: 200,
					statusText: "OK",
					headers: {"Content-Type": "application/json"},
				});
			}
		} catch (error) {
			console.warn("CORS proxy failed:", error);
		}
	}

	// If all else fails, throw the original error
	throw new Error(
		`Failed to fetch ${url}: CORS policy blocked the request and all fallback strategies failed`,
	);
}

/**
 * Specific function for fetching theme files with better error handling
 */
export async function fetchThemeFile(
	url: string,
	fetchFn: typeof fetch = fetch,
): Promise<any> {
	try {
		const response = await fetchWithCorsHandling(url, {}, fetchFn);

		// Handle opaque responses from no-cors mode
		if (response.type === "opaque") {
			console.warn(
				"Received opaque response - cannot parse theme file. Using empty theme.",
			);
			return {};
		}

		const text = await response.text();
		return JSON.parse(text);
	} catch (error) {
		console.error("Failed to fetch theme file:", error);
		console.warn("Using empty theme as fallback");
		return {}; // Return empty theme as fallback
	}
}
